// src/products/performance/hooks/labelDraft.ts
import { useSyncExternalStore } from "react";
import type {
  LevelKey,
  PerformanceConfig,
  RoleKey,
  TermKey,
} from "../../../../shared/products/performance/config";

/**
 * The unsaved Roles and wording draft, for this browser tab only (E9 operator ruling O-5): while HR
 * types on Settings › Roles and wording, the real sidebar and every label hook read the draft, as
 * the wireframe re-labels the navigation on each keystroke (views-settings.js:23, 135-144).
 *
 * Client-only and in memory: nothing is stored or sent, other users never see it, and a reload
 * forgets it. The Roles and wording tab publishes it and clears it on Discard, on leaving the tab
 * or the page, and once a save has landed; it applies only to the instance it was made on.
 */
export interface LabelDraft {
  instanceId: string;
  roles: Record<RoleKey, string>;
  levels: Record<LevelKey, string>;
  terms: Record<TermKey, string>;
}

let current: LabelDraft | null = null;
const listeners = new Set<() => void>();

export function setLabelDraft(draft: LabelDraft | null): void {
  if (draft === current) return;
  current = draft;
  for (const listener of listeners) listener();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const snapshot = () => current;

/** The draft for `instanceId`, or null (none, or made on another instance). */
export function useLabelDraft(instanceId: string | null): LabelDraft | null {
  const draft = useSyncExternalStore(subscribe, snapshot, snapshot);
  return draft && instanceId && draft.instanceId === instanceId ? draft : null;
}

/** `config` with the draft's roles, levels and words laid over it; the same object when none. */
export function withLabelDraft<T extends PerformanceConfig | undefined>(
  config: T,
  draft: LabelDraft | null,
): T {
  if (!config || !draft) return config;
  return { ...config, roles: draft.roles, levels: draft.levels, terms: draft.terms };
}
